/** SUNTRONIX ONE — AI Image Studio (Gemini Nano Banana).
 *  TECHNOLOGY → AI Image Studio. Type any prompt → generate & download images.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Loader2, Download, Trash2, ImagePlus, Wand2, Upload, X,
} from "lucide-react";

const ASPECTS = [
  { v: "square", l: "Square 1:1" },
  { v: "landscape", l: "Landscape" },
  { v: "portrait", l: "Portrait" },
];

function abs(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${API}${u}`;
}

function Card({ item, onDelete }) {
  const url = abs(item.url);
  const download = async () => {
    try {
      const res = await fetch(url, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `suntronix-ai-${item.id}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div data-testid={`studio-card-${item.id}`}
      className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col group">
      <div className="relative aspect-square bg-slate-50 border-b border-slate-100">
        <img src={url} alt={item.prompt} className="w-full h-full object-contain p-2" />
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-violet-600 text-white shadow-sm">
          <Sparkles className="h-2.5 w-2.5" /> AI
        </span>
      </div>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="text-[12px] text-slate-600 line-clamp-2" title={item.prompt}>{item.prompt}</div>
        <div className="mt-auto flex items-center justify-between">
          <Badge className="text-[10px] border bg-violet-100 text-violet-800 border-violet-200">
            {item.edited ? "Edited" : "Generated"}
          </Badge>
          <div className="flex items-center gap-1">
            <button onClick={download} title="Download" data-testid={`studio-download-${item.id}`}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
              <Download className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onDelete(item)} title="Delete" data-testid={`studio-delete-${item.id}`}
              className="p-1.5 rounded hover:bg-rose-50 text-rose-500">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AiImageStudio() {
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("square");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [access, setAccess] = useState(null);
  const [refImage, setRefImage] = useState(null); // {name, b64}
  const fileRef = useRef(null);

  const loadHistory = useCallback(() => {
    api.get("/image-ai/history").then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/image-ai/access").then(({ data }) => setAccess(data)).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const pickRef = (files) => {
    const f = files && files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setRefImage({ name: f.name, b64: String(reader.result) });
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if (prompt.trim().length < 3) { toast.error("Enter a longer prompt"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/image-ai/generate", {
        prompt, aspect, reference_image: refImage?.b64 || undefined,
      });
      toast.success(`Generated ${data.items?.length || 0} image(s)`);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const del = async (item) => {
    try { await api.delete(`/image-ai/studio/${item.id}`); setItems((x) => x.filter((i) => i.id !== item.id)); }
    catch { toast.error("Delete failed"); }
  };

  const disabled = access && access.llm_status !== "ACTIVE";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="ai-image-studio">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-violet-600 text-white grid place-items-center">
          <Sparkles className="h-6 w-6" strokeWidth={1.6} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">AI Image Studio</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Generate images from a prompt with {access?.model_label || "Gemini Nano Banana"} · marketing, banners, mockups.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="studio-composer">
        <Textarea
          data-testid="studio-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe the image you want… e.g. 'A clean hero banner of a modern laptop on a desk, soft daylight, minimalist, blue accent'"
          className="min-h-[96px]"
        />
        <div className="flex flex-wrap items-center gap-2">
          {ASPECTS.map((a) => (
            <button key={a.v} onClick={() => setAspect(a.v)} data-testid={`studio-aspect-${a.v}`}
              className={"text-xs px-3 py-1.5 rounded-full border transition-colors " +
                (aspect === a.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
              {a.l}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            {refImage ? (
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 rounded-full pl-2.5 pr-1 py-1" data-testid="studio-ref-chip">
                <Wand2 className="h-3 w-3" /> {refImage.name.slice(0, 18)}
                <button onClick={() => setRefImage(null)} className="p-0.5 rounded-full hover:bg-slate-200"><X className="h-3 w-3" /></button>
              </span>
            ) : (
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="studio-ref-upload">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Reference (edit)
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => pickRef(e.target.files)} />
            <Button onClick={generate} disabled={busy || disabled} data-testid="studio-generate"
              className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />}
              Generate
            </Button>
          </div>
        </div>
        {disabled && (
          <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            AI image generation is not configured (EMERGENT_LLM_KEY missing).
          </div>
        )}
      </div>

      <div data-testid="studio-gallery">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Your recent generations</div>
        {items.length === 0 ? (
          <div className="py-16 text-center text-slate-400 border border-dashed border-slate-200 rounded-xl">
            No images yet — write a prompt above and hit Generate.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {items.map((it) => <Card key={it.id} item={it} onDelete={del} />)}
          </div>
        )}
      </div>
    </div>
  );
}
